import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { content, MAP_URL } from '../translations/content';
import { motion } from 'framer-motion';
import { fadeInUp, staggerContainer } from '../utils/animations';
import { MapPinIcon, PhoneIcon, EnvelopeIcon } from '@heroicons/react/24/outline';

const Footer = () => {
  const { language } = useLanguage();
  const { footer, nav, contact } = content[language];

  return (
    <motion.footer
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      variants={staggerContainer}
      className="bg-gradient-to-br from-gray-900 to-gray-800 text-white relative overflow-hidden"
    >
      <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:20px_20px]"></div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 py-16">
          {/* About Section */}
          <motion.div variants={fadeInUp} className="space-y-4">
            <a href="/" className="block">
              <span className="text-xl font-bold text-white hover:text-gray-200 transition-colors duration-200">
                {nav.logo}
              </span>
            </a>
            <p className="text-gray-300 mt-4">
              {footer.description}
            </p>
            <div className="flex space-x-4 mt-6">
              <a href="https://www.facebook.com/tandartspraktijkassenberg" className="text-gray-400 hover:text-white transition-colors duration-200 hover:scale-110 transform">
                <span className="sr-only">Facebook</span>
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" />
                </svg>
              </a>
              <a href="https://www.instagram.com/tandartspraktijkassenberg" className="text-gray-400 hover:text-white transition-colors duration-200 hover:scale-110 transform">
                <span className="sr-only">Instagram</span>
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" />
                </svg>
              </a>
            </div>
          </motion.div>

          {/* Services Links */}
          <motion.div variants={fadeInUp}>
            <h3 className="text-lg font-semibold mb-4">{content[language].services.title}</h3>
            <ul className="space-y-2">
              {content[language].services.list.slice(0, 4).map((service, index) => (
                <li key={index}>
                  <a
                    href={`#service-${index}`}
                    className="text-gray-300 hover:text-white transition-colors duration-200 hover:translate-x-1 transform inline-block"
                  >
                    {service.title}
                  </a>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Contact Info */}
          <motion.div variants={fadeInUp}>
            <h3 className="text-lg font-semibold mb-4">{contact.title}</h3>
            <ul className="space-y-4 text-gray-300">
              <li className="flex items-start">
                <MapPinIcon className="h-6 w-6 text-primary-400 mr-3 flex-shrink-0" />
                <a href={MAP_URL} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors duration-200">
                  <div>
                    <p>{contact.address.street}</p>
                    <p>{contact.address.city}</p>
                    <p>{contact.address.country}</p>
                  </div>
                </a>
              </li>
              <li className="flex items-center">
                <PhoneIcon className="h-6 w-6 text-primary-400 mr-3 flex-shrink-0" />
                <a href={`tel:${contact.phoneNumber}`} className="hover:text-white transition-colors duration-200">
                  {contact.phoneNumber}
                </a>
              </li>
              <li className="flex items-center">
                <EnvelopeIcon className="h-6 w-6 text-primary-400 mr-3 flex-shrink-0" />
                <a href={`mailto:${contact.emailAddress}`} className="hover:text-white transition-colors duration-200">
                  {contact.emailAddress}
                </a>
              </li>
            </ul>
          </motion.div>

          {/* Opening Hours */}
          <motion.div variants={fadeInUp}>
            <h3 className="text-lg font-semibold mb-4">{contact.hours.title}</h3>
            <p className="text-gray-300">{contact.hours.schedule}</p>
            <div className="mt-4">
              <img
                src="https://tandartspraktijkassenberg.nl/.cm4all/iproc.php/tcbruggehoofdfoto.jpg/downsize_1280_0/tcbruggehoofdfoto.jpg"
                alt="Tandartspraktijk Assenberg Building"
                className="w-full h-32 object-cover rounded-lg shadow-lg"
              />
            </div>
          </motion.div>
        </div>

        {/* Copyright */}
        <div className="border-t border-gray-700 py-6 text-center text-gray-400">
          <p>&copy;{footer.rights}</p>
        </div>
      </div>
    </motion.footer>
  );
};

export default Footer;
